'use client';

import { Capacitor } from '@capacitor/core';
import { cn, Iphone15Pro } from '@up2stay/up2promo-ui';
import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

import { isCapacitorNative } from '@/core/utils/capacitor.utils';

const IOS_SCREEN_WIDTH = 389.5;
const IOS_SCREEN_HEIGHT = 843.5;

/**
 * Stick the desktop preview rail in the ion-content scroller so the left
 * column can move while the phone stays in view. Native shells pin the header,
 * so the offset sits below that chrome.
 */
export function stickyPhonePreviewRailClass(isNativeShell: boolean): string {
  return cn(
    'sticky z-10 self-start',
    isNativeShell
      ? 'top-[max(1rem,calc(env(safe-area-inset-top,0px)+5.5rem))]'
      : 'top-4',
  );
}

/**
 * Phone chrome cap (448px). Larger than the old 380px edit max. Do not use
 * `min(100%, …)` here — a percentage width on an `auto` grid column is
 * indefinite and collapses the absolutely-empty box to 0×0.
 */
export const PHONE_PREVIEW_MAX_WIDTH_CLASS = 'max-w-[28rem]';

/** Phone + 1.5rem pad each side. Fixed rem so the grid track stays definite. */
export const PROFILE_PREVIEW_RAIL_WIDTH_CLASS = 'w-[31rem] shrink-0';

/**
 * Shared 1fr + rail split — use `xl:` so the 31rem phone rail only appears when
 * the shell sidebar may already reserve space (SHELL_SIDEBAR_OVERLAY_BREAKPOINT).
 */
export const PROFILE_PREVIEW_SPLIT_COLS = 'minmax(0,1fr)_31rem';

export const PROFILE_OVERVIEW_SPLIT_GRID_CLASS =
  'grid min-w-0 flex-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_31rem] xl:gap-[26px]';

export const PROFILE_EDIT_SPLIT_GRID_CLASS =
  'grid min-h-0 min-w-0 flex-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_31rem]';

const PHONE_PREVIEW_FIT_STYLE: CSSProperties = {
  width: '100%',
  height: 'auto',
  display: 'block',
  maxHeight: 'min(92vh, calc(100dvh - 5rem))',
};

export type PhonePreviewFrameProps = {
  children: ReactNode;
  className?: string;
  /** Extra max-height on the device chrome (feedback editors). */
  maxHeightClassName?: string;
};

/**
 * Shared Iphone15Pro chrome for profile + feedback previews.
 * Feature modules supply screen content only.
 *
 * The SVG stays in-flow (intrinsic 433×882). Absolute fill was removed
 * because a 0-height parent then clipped the whole preview.
 */
export function PhonePreviewFrame({
  children,
  className,
  maxHeightClassName,
}: PhonePreviewFrameProps) {
  const iosViewportRef = useRef<HTMLDivElement | null>(null);
  const [iosPreviewScale, setIosPreviewScale] = useState(1);

  const isNativeIOS = useMemo(
    () => isCapacitorNative() && Capacitor.getPlatform() === 'ios',
    [],
  );

  useLayoutEffect(() => {
    if (!isNativeIOS) return;
    const viewport = iosViewportRef.current;
    if (!viewport) return;

    const updateScale = () => {
      const { width, height } = viewport.getBoundingClientRect();
      if (width <= 0 || height <= 0) return;
      const nextScale = Math.min(
        width / IOS_SCREEN_WIDTH,
        height / IOS_SCREEN_HEIGHT,
      );
      setIosPreviewScale((prev) =>
        Math.abs(prev - nextScale) < 0.001 ? prev : nextScale,
      );
    };

    updateScale();
    const observer = new ResizeObserver(() => updateScale());
    observer.observe(viewport);

    return () => {
      observer.disconnect();
    };
  }, [isNativeIOS]);

  const deviceClassName = cn(
    'h-auto w-full',
    PHONE_PREVIEW_MAX_WIDTH_CLASS,
    maxHeightClassName,
  );

  return (
    <div
      className={cn(
        'relative flex w-full min-w-0 items-center justify-center',
        className,
      )}
    >
      {isNativeIOS ? (
        <div
          className={cn(
            'relative mx-auto w-full',
            PHONE_PREVIEW_MAX_WIDTH_CLASS,
          )}
          data-slot="phone-preview-stage"
        >
          <Iphone15Pro
            className={cn(deviceClassName, 'pointer-events-none')}
            preserveAspectRatio="xMidYMid meet"
            style={PHONE_PREVIEW_FIT_STYLE}
          />
          <div
            ref={iosViewportRef}
            className="absolute overflow-hidden"
            // WKWebView has foreignObject sizing bugs; clip screen content via CSS on iOS.
            style={{
              left: '4.9076%',
              top: '2.1825%',
              width: '89.9538%',
              height: '95.6349%',
              borderRadius: '14.3132% / 6.6115%',
            }}
          >
            <div
              style={{
                width: `${IOS_SCREEN_WIDTH}px`,
                height: `${IOS_SCREEN_HEIGHT}px`,
                transform: `scale(${iosPreviewScale})`,
                transformOrigin: 'top left',
                willChange: 'transform',
              }}
            >
              {children}
            </div>
          </div>
        </div>
      ) : (
        <div
          className={cn(
            'relative mx-auto w-full',
            PHONE_PREVIEW_MAX_WIDTH_CLASS,
          )}
          data-slot="phone-preview-stage"
        >
          <Iphone15Pro
            className={deviceClassName}
            preserveAspectRatio="xMidYMid meet"
            style={PHONE_PREVIEW_FIT_STYLE}
          >
            {children}
          </Iphone15Pro>
        </div>
      )}
    </div>
  );
}
